/**
 * 画布 LLM 生成器（供链式执行引擎调用）
 *
 * 复用 /api/canvas/llm/stream 接口，将结果写回 LLM 节点的 data.output。
 * 不处理 UI 状态（如流式预览），仅做纯执行。
 * 调用参数统一由 resolveLlmInvocation 解析（上游文本 + 图片资产 + 输入框 @图N），
 * 与 LLM 节点按钮同口径。
 */
import { boardActions, getBoardState } from '../state/boardStore';
import { waitForUpstreamIdle } from '../state/upstream';
import { resolveLlmInvocation } from '../state/llmInvocation';
import { reversePromptStream, toVisionFetchableRef } from '../services/visionApi';
import { readSSEStream } from '../sseStream';
import { platformFetch } from '../../../platform/http';
import { CANVAS_NODE_REMOVED } from '../utils';
import toast from '../../../components/Toast';
import type { CanvasNode, LlmNodeData } from '../types';

export async function runLlmGeneration(nodeId: string): Promise<void> {
  // 等待后重读最新图状态
  await waitForUpstreamIdle(nodeId);
  const { nodes, edges } = getBoardState();
  const node = nodes.find((n) => n.id === nodeId) as CanvasNode<LlmNodeData> | undefined;
  if (!node) throw new Error('节点不存在');

  const data = node.data;

  // 如果正在生成中，跳过
  if (data.generating) return;

  const input = (data.input || '').trim();
  // 统一解析：上游文本 + 图片资产 + 输入框 @图N（与 LLM 节点按钮同口径）
  const invocation = resolveLlmInvocation(nodeId, nodes, edges, input);
  if (invocation.warnings.length > 0) toast(invocation.warnings.join('；'));
  if (!invocation.hasInput) return;

  boardActions.updateNodeData(nodeId, { generating: true, errorMessage: null, output: '' });

  try {
    let accumulated = '';
    const onDelta = (delta: string) => {
      // 节点被删除/项目被切换即停止消费流（抛错会让 readSSEStream cancel 底层连接，
      // 后端 req close 即中止上游 LLM 调用，不再白白消耗流量）
      if (!getBoardState().nodes.some((n) => n.id === nodeId)) {
        throw new Error(CANVAS_NODE_REMOVED);
      }
      accumulated += delta;
      boardActions.updateNodeData(nodeId, { output: accumulated });
    };

    if (invocation.isVision) {
      // 反推模式：blob 引用先转 data URL，服务端 vision 按图集流式反推
      const images: string[] = [];
      for (const url of invocation.images) {
        images.push(await toVisionFetchableRef(url));
      }
      await reversePromptStream({ images, instruction: invocation.instruction, onDelta });
    } else {
      // process 通用模式：与 LLM 节点按钮同一路径，行为由输入与指令驱动
      const body: Record<string, string> = { mode: 'process' };
      if (invocation.upstreamText) body.upstreamText = invocation.upstreamText;
      if (invocation.resolvedInput) body.instruction = invocation.resolvedInput;
      body.prompt = invocation.upstreamText
        ? invocation.resolvedInput || invocation.upstreamText
        : invocation.resolvedInput;

      const response = await platformFetch('/canvas/llm/stream', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || '生成失败');
      }

      await readSSEStream(response, (payload) => {
        if (payload === '[DONE]') return;
        const parsed = JSON.parse(payload);
        if (parsed.error) throw new Error(parsed.error);
        if (parsed.content) {
          onDelta(parsed.content);
        }
      });
    }

    boardActions.updateNodeData(nodeId, { output: accumulated, generating: false });
    boardActions.commitNodeMutation();
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      boardActions.updateNodeData(nodeId, { generating: false });
      return;
    }
    // 节点已被删除：主动取消，不写状态、不上抛（哨兵不暴露给链式执行的 toast）
    if (err instanceof Error && err.message === CANVAS_NODE_REMOVED) return;
    boardActions.updateNodeData(nodeId, {
      generating: false,
      errorMessage: (err as Error).message || '生成失败',
    });
    // 链路语义：失败上抛中断后续节点（executeChain 及 UI 调用方均已兜底 toast）
    throw err;
  }
}
